import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Label } from "@/ui/components/ui/label";
import { Slider } from "@/ui/components/ui/slider";
import { Switch } from "@/ui/components/ui/switch";
import { useTimelineTrackSettingsStore, TrackSettings } from "@/shared/stores/useTimelineTrackSettingsStore";
import { trackLabel } from "@/machines/utils/track-label.ts";

interface TrackSettingsModalPayload {
  availableTracks: {
    key: string;
    label: string;
    defaultHeight: number;
  }[];
}

interface TimelineTrackSettingsModalProps {
  payload: TrackSettingsModalPayload;
  onClose: () => void;
}

const TimelineTrackSettingsModal: React.FC<TimelineTrackSettingsModalProps> = ({
  payload,
  onClose,
}) => {
  const { t } = useTranslation("machines");
  const { availableTracks } = payload;
  const [expandedTracks, setExpandedTracks] = useState<Set<string>>(new Set());

  const {
    trackSettings,
    setTrackVisible,
    setTrackHeight,
    resetTrackSettings: resetStoreSettings,
    initializeTrack,
  } = useTimelineTrackSettingsStore();

  // Initialize tracks if not already in store
  availableTracks.forEach((track) => {
    initializeTrack(track.key, track.defaultHeight);
  });

  // Local temporary state for uncommitted changes
  const [tempSettings, setTempSettings] = useState<Record<string, TrackSettings>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize temp settings from store on mount
  useEffect(() => {
    const initialSettings: Record<string, TrackSettings> = {};
    availableTracks.forEach((track) => {
      initialSettings[track.key] = trackSettings[track.key] || {
        visible: true,
        height: track.defaultHeight,
      };
    });
    setTempSettings(initialSettings);
  }, []);

  // Update temp settings functions
  const updateTempVisible = (trackKey: string, visible: boolean) => {
    setTempSettings((prev) => ({
      ...prev,
      [trackKey]: {
        ...prev[trackKey],
        visible,
      },
    }));
  };

  const updateTempHeight = (trackKey: string, height: number) => {
    setTempSettings((prev) => ({
      ...prev,
      [trackKey]: {
        ...prev[trackKey],
        height: Math.max(10, Math.min(200, height)),
      },
    }));
  };

  const toggleTrackExpanded = (trackKey: string) => {
    setExpandedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(trackKey)) {
        next.delete(trackKey);
      } else {
        next.add(trackKey);
      }
      return next;
    });
  };

  const handleToggleAll = (visible: boolean) => {
    const updatedSettings: Record<string, TrackSettings> = {};
    availableTracks.forEach((track) => {
      updatedSettings[track.key] = {
        ...tempSettings[track.key],
        visible,
      };
    });
    setTempSettings((prev) => ({ ...prev, ...updatedSettings }));
  };

  const resetTrackSettings = () => {
    const resetSettings: Record<string, TrackSettings> = {};
    availableTracks.forEach((track) => {
      resetSettings[track.key] = {
        visible: true,
        height: track.defaultHeight,
      };
    });
    setTempSettings(resetSettings);
  };

  const handleSave = () => {
    setIsSaving(true);

    // Apply all temp settings to the store
    Object.entries(tempSettings).forEach(([trackKey, settings]) => {
      setTrackVisible(trackKey, settings.visible);
      setTrackHeight(trackKey, settings.height);
    });

    // Close immediately - rendering is instant now
    setIsSaving(false);
    onClose();
  };

  const handleCancel = () => {
    // Discard changes and close
    onClose();
  };

  const visibleCount = availableTracks.filter(
    (track) => tempSettings[track.key]?.visible !== false
  ).length;

  return (
    <div className="flex flex-col w-full space-y-4">
      {/* Header with counts */}
      <div className="text-sm text-muted-foreground">
        {t("trackSettings.showingCount", { visible: visibleCount, total: availableTracks.length })}
      </div>

      {/* Global Actions */}
      <div className="flex items-center justify-between gap-2 pb-4 border-b">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleToggleAll(true)}
          >
            {t("trackSettings.showAll")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleToggleAll(false)}
          >
            {t("trackSettings.hideAll")}
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetTrackSettings}
        >
          {t("trackSettings.resetToDefaults")}
        </Button>
      </div>

      {/* Track List */}
      <div className="overflow-y-auto max-h-[60vh] space-y-2 pr-2">
        {availableTracks.map((track) => {
          const settings = tempSettings[track.key] || {
            visible: true,
            height: track.defaultHeight,
          };
          const isExpanded = expandedTracks.has(track.key);

          return (
            <div
              key={track.key}
              className="border rounded-lg p-3 space-y-3 bg-card"
            >
              {/* Track Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Switch
                    checked={settings.visible}
                    onCheckedChange={(checked) =>
                      updateTempVisible(track.key, checked)
                    }
                    id={`track-visible-${track.key}`}
                  />
                  <Label
                    htmlFor={`track-visible-${track.key}`}
                    className="font-medium cursor-pointer flex items-center gap-2"
                  >
                    {settings.visible ? (
                      <Eye className="h-4 w-4 text-green-600" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    )}
                    {trackLabel(track.label, t)}
                    <span className="text-xs text-muted-foreground font-normal">
                      {t("trackSettings.heightSuffix", { value: settings.height })}
                    </span>
                  </Label>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleTrackExpanded(track.key)}
                  className="h-8 w-8 p-0"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Expanded Settings */}
              {isExpanded && (
                <div className="pt-2 border-t space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor={`track-height-${track.key}`}
                        className="text-sm"
                      >
                        {t("trackSettings.trackHeight")}
                      </Label>
                      <span className="text-sm text-muted-foreground">
                        {t("trackSettings.pixels", { value: settings.height })}
                      </span>
                    </div>
                    <Slider
                      id={`track-height-${track.key}`}
                      value={[settings.height]}
                      onValueChange={(value) =>
                        updateTempHeight(track.key, value[0])
                      }
                      min={10}
                      max={200}
                      step={5}
                      className="w-full"
                      disabled={!settings.visible}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t("trackSettings.defaultHeight", { value: track.defaultHeight })}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() =>
                        updateTempHeight(track.key, track.defaultHeight)
                      }
                    >
                      {t("trackSettings.reset")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
          {t("actions.cancel")}
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("trackSettings.applying")}
            </>
          ) : (
            t("trackSettings.save")
          )}
        </Button>
      </div>
    </div>
  );
};

export default TimelineTrackSettingsModal;
